/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery } from "@tanstack/react-query";
import { client } from "services/api-client";

export const useGetCashCenters = () =>
  useQuery<any>({
    queryKey: ["GetCashCenters"],
    queryFn: () => client(`lookups/cash/centers`)
  });

export const useGetCustomers = () =>
  useQuery<any>({
    queryKey: ["GetVaultCustomers"],
    queryFn: () => client(`lookups/customers`),
    refetchOnWindowFocus: false
  });

export const useGetVaultCurrencies = () =>
  useQuery<any[]>({
    queryKey: ["GetVaultCurrencies"],
    queryFn: () => client(`lookups/currencies`)
  });

export const useGetVaultDenominations = () =>
  useMutation({
    mutationFn: (shortName: string) =>
      client(`lookups/currency/denominations/${shortName}`)
  });

export const useGetCoinsDenominations = () =>
  useQuery({
    queryKey: ["GetCoinsDenom"],
    queryFn: () => client(`lookups/coins/denominations`)
  });

export const useGetVehicles = (cashCenterId: string) =>
  useQuery<any[]>({
    queryKey: ["GetVehicles", cashCenterId],
    queryFn: () => client(`lookups/cash/vans/${cashCenterId}`)
  });

export const useGetRouteTeams = () =>
  useQuery<any[]>({
    queryKey: ["GetRouteTeams"],
    queryFn: () => client(`daily/operations/routes/list`)
  });

export const useGetServicePoints = () =>
  useMutation({
    mutationFn: (customerId: string) =>
      client(`lookups/customer/cit/sites/${customerId}`)
  });

export const useGetShiftRoutes = (data: any) =>
  useQuery<any[]>({
    queryKey: ["GetRouteTeamLeaders"],
    queryFn: () =>
      client(`daily/operations/dispatched/field/teams/list`, { data })
  });

export const useGetPendingReasons = () =>
  useQuery<any[]>({
    queryKey: ["GetPendingReasons"],
    queryFn: () => client(`lookups/pending/reasons/CIT`)
  });

export const useGetCurrenciesList = () =>
  useQuery({
    queryKey: ["GetCurrenciesList"],
    queryFn: () => client(`lookups/currencies`)
  });

export const useGetExchangeRate = () =>
  useMutation({
    mutationFn: (currency: string) =>
      client(`lookups/exchange/rate/${currency}`)
  });

export const useGetOffices = () =>
  useMutation({
    mutationFn: (cashCenterId: string) =>
      client(`lookups/cash/center/offices/${cashCenterId}`)
  });

export const useGetCities = (city: string) =>
  useQuery({
    queryKey: ["GetCities", city],
    queryFn: () => client(`lookups/cities/${city}`)
  });

export const useGetRegions = (state: string) =>
  useQuery({
    queryKey: ["GetRegions", state],
    queryFn: () => client(`lookups/regions/${state}`),
    refetchOnWindowFocus: false
  });

export const useGetUsersByRole = (cashCenterId: string, role: string) =>
  useQuery({
    queryKey: ["GetUsersByRole", cashCenterId, role],
    queryFn: () => client(`auth/users/list/${cashCenterId}/${role}`)
  });

export const useGetInRouteTeam = (data: any) =>
  useQuery<any[]>({
    queryKey: ["GetInRouteTeam", data?.cashCenterId, data?.operationDate],
    queryFn: () => client(`daily/operations/inroute/teams`, { data }),
    enabled: Boolean(data?.cashCenterId)
  });

export const useGetATMList = () =>
  useQuery({
    queryKey: ["GetATMLis"],
    queryFn: () => client(`lookups/atms/list`)
  });

export const useGetModelsList = (createType?: string) =>
  useQuery({
    queryKey: ["GetModelsList"],
    queryFn: () => client(`lookups/replenish/models`),
    enabled: createType ? createType === "multiple" : true
  });

export const useGetKioskATMList = () =>
  useMutation({
    mutationFn: (customerId: string) =>
      client(`lookups/customer/kiosk/sites/${customerId}`)
  });

export const useGetCashierLoads = (data: any) =>
  useQuery({
    queryKey: [
      "GetCashierLoads",
      data?.cashCenterId,
      data?.operationDate,
      data?.serviceType
    ],
    queryFn: () => client(`statistics/processing/cashiers/load`, { data })
  });

export const useGetDeliveryCashiersLoad = (data: any) =>
  useQuery({
    queryKey: [
      "GetDeliveryCashiersLoad",
      data?.operationDate,
      data?.serviceType
    ],
    queryFn: () => client(`statistics/packing/cashiers/load`, { data })
  });

export const useGetAssignmentGroups = () =>
  useQuery({
    queryKey: ["AssignmentGroups"],
    queryFn: () => client(`lookups/assignment/groups`),
    refetchOnWindowFocus: false
  });

interface FieldEngineer {
  employeeId: string;
  name: string;
  nationality: string;
  cenconId: string;
}

export const useGetFieldEngineers = () =>
  useQuery<FieldEngineer[]>({
    queryKey: ["FieldEngineers"],
    queryFn: () =>
      client("auth/field/team").then((res: { data: FieldEngineer[] }) => {
        const allOption: FieldEngineer = {
          employeeId: "all",
          name: "All",
          nationality: "",
          cenconId: ""
        };
        return [allOption, ...res.data];
      }),
    refetchOnWindowFocus: false
  });
